import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Download, Mail, Check, Loader } from 'lucide-react';
import { EASE_OUT } from '../../lib/anim';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TEXTURE_BG = 'https://static.prod-images.emergentagent.com/jobs/caf8da25-7ee7-4ec7-9081-85c371988941/images/59ceb0d8e9bfddbe418d9e334f1e7a5ab3d74b5664c84e18b3ed9b7f13faa98d.png';

const CARD_REVEAL = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: EASE_OUT },
};
const ONCE = { once: true };
const OVERLINE_REVEAL = { initial: { opacity: 0, y: 10 }, whileInView: { opacity: 1, y: 0 }, viewport: ONCE, transition: { delay: 0.2 } };
const HEADING_REVEAL = { initial: { opacity: 0, y: 15 }, whileInView: { opacity: 1, y: 0 }, viewport: ONCE, transition: { delay: 0.3, duration: 0.6 } };
const TEXT_REVEAL = { initial: { opacity: 0, y: 10 }, whileInView: { opacity: 1, y: 0 }, viewport: ONCE, transition: { delay: 0.4 } };
const CTA_REVEAL = {
  initial: { opacity: 0, y: 10 }, whileInView: { opacity: 1, y: 0 }, viewport: ONCE, transition: { delay: 0.5 },
  whileHover: { scale: 1.03 }, whileTap: { scale: 0.98 },
};

const PRIMARY_BTN = {
  display: 'inline-flex', alignItems: 'center', gap: '10px',
  padding: '14px 36px',
  background: 'linear-gradient(135deg, #F67425, #BB0518)',
  color: '#fff', border: 'none', borderRadius: '2px',
  fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 700,
  cursor: 'pointer', boxShadow: '0 4px 16px rgba(246,116,37,0.25)',
};

const recordConsent = (userId, source) => {
  axios.post(`${API}/users/${userId}/consent?source=${source}`).catch(() => {});
};

const generatePdf = async (userName) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  const element = document.getElementById('results-capture');
  if (!element) return;
  const firstName = (userName || 'Axpats').split(' ')[0];

  const canvas = await html2canvas(element, {
    scale: window.innerWidth < 768 ? 1.2 : 1.5,
    useCORS: true,
    logging: false,
    backgroundColor: '#FDF9F4',
    onclone: (doc) => {
      const style = doc.createElement('style');
      style.textContent = `
        #results-capture, #results-capture * {
          opacity: 1 !important;
          transform: none !important;
          transition: none !important;
          animation: none !important;
        }
      `;
      doc.head.appendChild(style);
      doc.querySelectorAll('#results-capture *').forEach((el) => {
        const s = el.style;
        // html2canvas renders background-clip:text as invisible text — replace with solid brand color
        if (s.webkitTextFillColor === 'transparent' || s.webkitBackgroundClip === 'text' || s.backgroundClip === 'text') {
          s.webkitTextFillColor = '';
          s.webkitBackgroundClip = '';
          s.backgroundClip = '';
          s.background = 'none';
          s.color = '#F67425';
        }
        // html2canvas mispositions baseline-aligned flex items — text lands outside the box
        if (s.alignItems === 'baseline') s.alignItems = 'center';
        // blend modes & backdrop filters are unsupported — drop decorative layers
        if (s.mixBlendMode && s.mixBlendMode !== 'normal') s.display = 'none';
        if (s.backdropFilter || s.webkitBackdropFilter) {
          s.backdropFilter = 'none';
          s.webkitBackdropFilter = 'none';
        }
      });
      // html2canvas can't lay out this inline-flex/baseline card — rebuild it as simple inline markup
      doc.querySelectorAll('[data-pdf-statcard]').forEach((card) => {
        const num = card.children[0]?.textContent || '';
        const label = card.children[1]?.textContent || '';
        card.setAttribute('style', 'display:table;margin:0 auto;text-align:center;padding:14px 32px;background:#fff;border:1px solid rgba(48,43,44,0.1);border-radius:2px;');
        card.innerHTML =
          '<span style="font-family:var(--font-heading);font-size:44px;font-weight:900;color:#F67425;vertical-align:middle;">' + num + '</span>' +
          '<span style="font-family:var(--font-body);font-size:18px;color:rgba(48,43,44,0.72);font-weight:500;margin-left:10px;vertical-align:middle;">' + label + '</span>';
      });
    },
  });

  // Single page sized exactly to content — no arbitrary page cuts, no trailing blank space
  const pdf = new jsPDF({
    orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
    unit: 'px',
    format: [canvas.width, canvas.height],
    hotfixes: ['px_scaling'],
    compress: true,
  });
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, canvas.width, canvas.height);
  pdf.save(`Axpats_Report_${firstName}.pdf`);
};

const DownloadPdfButton = ({ userName, userId, consentOk, onNeedConsent }) => {
  const [state, setState] = useState('idle'); // idle | working | done | error

  const handleDownload = async () => {
    if (state === 'working') return;
    if (!consentOk) { onNeedConsent(); return; }
    setState('working');
    try {
      if (userId) recordConsent(userId, 'pdf_download');
      await generatePdf(userName);
      setState('done');
    } catch (err) {
      console.error('PDF generation failed', err);
      setState('error');
    }
  };

  const label = state === 'working' ? 'Preparing PDF...' : state === 'done' ? 'Downloaded — Save Again?' : state === 'error' ? 'Try Again' : 'Download PDF Report';

  return (
    <motion.button
      data-testid="download-pdf-btn"
      {...CTA_REVEAL}
      onClick={handleDownload}
      disabled={state === 'working'}
      style={{ ...PRIMARY_BTN, opacity: consentOk ? 1 : 0.55, cursor: state === 'working' ? 'wait' : 'pointer' }}
    >
      {state === 'working' ? <Loader size={18} className="animate-spin" /> : <Download size={18} />}
      {label}
    </motion.button>
  );
};

const EmailReportButton = ({ assessmentId, userId, consentOk, onNeedConsent }) => {
  const [state, setState] = useState('idle');
  const [sentDirect, setSentDirect] = useState(false);

  const requestEmail = async () => {
    if (state === 'loading' || state === 'done') return;
    if (!consentOk) { onNeedConsent(); return; }
    setState('loading');
    try {
      const res = await axios.post(`${API}/report/email-request?assessment_id=${assessmentId}&user_id=${userId}`);
      setSentDirect(res.data?.status === 'sent');
      setState('done');
    } catch {
      setState('error');
    }
  };

  const label = state === 'done' ? (sentDirect ? 'Report Sent — Check Your Inbox' : 'Report Requested') : state === 'loading' ? 'Sending...' : state === 'error' ? 'Try Again' : 'Email My Report';

  return (
    <motion.button
      data-testid="email-report-btn"
      {...CTA_REVEAL}
      onClick={requestEmail}
      disabled={state === 'loading'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '10px',
        padding: '14px 32px',
        background: state === 'done' ? 'rgba(16,185,129,0.08)' : 'transparent',
        color: state === 'done' ? '#0B815A' : '#302B2C',
        border: state === 'done' ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(48,43,44,0.25)',
        borderRadius: '2px',
        fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 700,
        cursor: state === 'done' ? 'default' : 'pointer',
        opacity: consentOk ? 1 : 0.55,
      }}
    >
      {state === 'done' ? <Check size={18} /> : <Mail size={18} />}
      {label}
    </motion.button>
  );
};

const DownloadReport = ({ userName, assessmentId, userId, initialConsent = false }) => {
  const firstName = userName?.split(' ')[0] || '';
  const [consent, setConsent] = useState(!!initialConsent);
  const [highlight, setHighlight] = useState(false);

  const onNeedConsent = () => {
    setHighlight(true);
    setTimeout(() => setHighlight(false), 1600);
  };

  return (
    <section
      data-testid="download-report-section"
      style={{ padding: '0 1.5rem 6rem', maxWidth: '900px', margin: '0 auto' }}
    >
      <motion.div
        {...CARD_REVEAL}
        style={{
          position: 'relative', overflow: 'hidden', borderRadius: '2px',
          border: '1px solid rgba(48,43,44,0.1)', background: '#fff',
          boxShadow: '0 20px 50px -20px rgba(48,43,44,0.12)',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: `url(${TEXTURE_BG})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: 0.06, mixBlendMode: 'overlay',
        }} />
        <div style={{
          position: 'absolute', top: '-50%', right: '-20%', width: '400px', height: '400px',
          background: 'radial-gradient(circle, rgba(246,116,37,0.1) 0%, transparent 70%)',
          zIndex: 0,
        }} />

        <div style={{ position: 'relative', zIndex: 1, padding: '3rem 2.5rem', textAlign: 'center' }}>
          <motion.div
            {...OVERLINE_REVEAL}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '6px 16px',
              background: 'rgba(246,116,37,0.08)',
              border: '1px solid rgba(246,116,37,0.25)',
              borderRadius: '2px',
              marginBottom: '1.5rem',
            }}
          >
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: '0.65rem', fontWeight: 700,
              letterSpacing: '0.25em', textTransform: 'uppercase',
              color: '#F67425',
            }}>
              Keep Your Results
            </span>
          </motion.div>

          <motion.h2
            {...HEADING_REVEAL}
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
              fontWeight: 700, color: '#302B2C',
              letterSpacing: '-0.03em', lineHeight: 1.2,
              marginBottom: '1rem',
            }}
          >
            {firstName ? `${firstName}, take` : 'Take'} your report<br />
            with you
          </motion.h2>

          <motion.p
            {...TEXT_REVEAL}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '1rem',
              color: 'var(--text-secondary)',
              lineHeight: 1.7,
              maxWidth: '520px',
              margin: '0 auto 1.75rem',
            }}
          >
            Download this full assessment as a PDF, or have it delivered straight
            to your inbox — and our consultants will follow up with your next steps.
          </motion.p>

          <label
            data-testid="download-consent-label"
            style={{
              display: 'inline-flex', alignItems: 'flex-start', gap: '10px',
              maxWidth: '480px', margin: '0 auto 1.75rem', textAlign: 'left',
              fontSize: '0.8125rem', color: highlight ? '#BB0518' : 'var(--text-secondary)',
              lineHeight: 1.5, cursor: 'pointer',
              padding: '10px 14px', borderRadius: '2px',
              border: highlight ? '1px solid rgba(187,5,24,0.5)' : '1px solid rgba(48,43,44,0.12)',
              background: highlight ? 'rgba(187,5,24,0.04)' : 'var(--bg-subtle, #FAF6F1)',
              transition: 'border-color 0.3s, color 0.3s, background 0.3s',
            }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              data-testid="download-consent-checkbox"
              style={{ marginTop: '2px', accentColor: '#F67425', width: '16px', height: '16px', flexShrink: 0 }}
            />
            <span>
              I agree that Axpats may save my assessment data and contact me about my migration options.
            </span>
          </label>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <DownloadPdfButton userName={userName} userId={userId} consentOk={consent} onNeedConsent={onNeedConsent} />
            {assessmentId && userId && !String(assessmentId).startsWith('demo') && (
              <EmailReportButton assessmentId={assessmentId} userId={userId} consentOk={consent} onNeedConsent={onNeedConsent} />
            )}
          </div>

          <p style={{
            fontFamily: 'var(--font-body)', fontSize: '0.65rem', fontWeight: 600,
            color: 'var(--text-muted)', marginTop: '1rem',
            letterSpacing: '0.08em',
          }}>
            Powered by Axpats — your data stays private and secure
          </p>
        </div>
      </motion.div>
    </section>
  );
};

export default DownloadReport;
